import { createRouter, createWebHistory } from 'vue-router'
import { defineAsyncComponent } from 'vue'
import { Toast } from 'vant'
import LoadingSpinner from '@/components/LoadingSpinner.vue'
import ErrorFallback from '@/components/ErrorFallback.vue'

// 创建异步组件包装器，添加错误处理和加载状态
const createAsyncComponent = (importFunc) => {
  return defineAsyncComponent({
    loader: importFunc,
    loadingComponent: LoadingSpinner,
    errorComponent: ErrorFallback,
    delay: 200, // 延迟显示加载组件
    timeout: 10000, // 10秒超时
    suspensible: false,
    onError(error, retry, fail, attempts) {
      console.error(`Component loading failed (attempt ${attempts}):`, error)
      if (attempts <= 3) {
        // 重试最多3次
        retry()
      } else {
        fail()
      }
    }
  })
}

const routes = [
  {
    path: '/',
    name: 'Home',
    component: createAsyncComponent(() => import('../views/Home.vue'))
  },
  {
    path: '/news',
    name: 'News',
    component: createAsyncComponent(() => import('../views/News.vue'))
  },
  {
    path: '/about',
    name: 'About',
    component: createAsyncComponent(() => import('../views/About.vue'))
  },
  {
    path: '/news/:id',
    name: 'NewsDetail',
    component: createAsyncComponent(() => import('../views/NewsDetail.vue'))
  },
  {
    path: '/profile',
    name: 'Profile',
    component: createAsyncComponent(() => import('../views/Profile.vue'))
  },
  {
    path: '/products',
    name: 'Products',
    component: createAsyncComponent(() => import('../views/Products.vue'))
  },
  {
    path: '/product-detail',
    name: 'ProductDetail',
    component: createAsyncComponent(() => import('../views/ProductDetail.vue'))
  },
  {
    path: '/customize-form',
    name: 'CustomizeForm',
    component: createAsyncComponent(() => import('../views/CustomizeForm.vue'))
  },
  {
    path: '/customize-agreement',
    name: 'CustomizeAgreement',
    component: createAsyncComponent(() => import('../views/CustomizeAgreement.vue'))
  },
  {
    path: '/product-customize',
    name: 'ProductCustomize',
    component: createAsyncComponent(() => import('../views/ProductCustomize.vue'))
  },
  {
    path: '/order-confirm',
    name: 'OrderConfirm',
    component: createAsyncComponent(() => import('../views/OrderConfirm.vue'))
  },
  {
    path: '/order-create',
    name: 'OrderCreate',
    component: createAsyncComponent(() => import('../views/OrderCreate.vue'))
  },
  {
    path: '/order-success',
    name: 'OrderSuccess',
    component: createAsyncComponent(() => import('../views/OrderSuccess.vue'))
  },
  {
    path: '/order-list',
    name: 'OrderList',
    component: createAsyncComponent(() => import('../views/OrderList.vue'))
  },
  {
    path: '/order-status',
    name: 'OrderStatus',
    component: createAsyncComponent(() => import('../views/OrderStatus.vue'))
  },
  {
    path: '/pending-payment',
    name: 'PendingPayment',
    component: createAsyncComponent(() => import('../views/PendingPayment.vue'))
  },
  {
    path: '/pending-shipment',
    name: 'PendingShipment',
    component: createAsyncComponent(() => import('../views/PendingShipment.vue'))
  },
  {
    path: '/pending-receive',
    name: 'PendingReceive',
    component: createAsyncComponent(() => import('../views/PendingReceive.vue'))
  },
  {
    path: '/completed-orders',
    name: 'CompletedOrders',
    component: createAsyncComponent(() => import('../views/CompletedOrders.vue'))
  },
  {
    path: '/order-detail',
    name: 'OrderDetail',
    component: createAsyncComponent(() => import('../views/OrderDetail.vue'))
  },
  {
    path: '/logistics-detail',
    name: 'LogisticsDetail',
    component: createAsyncComponent(() => import('../views/LogisticsDetail.vue'))
  },

  {
    path: '/worldfirst-callback',
    name: 'WorldFirstCallback',
    component: createAsyncComponent(() => import('../views/WorldFirstCallback.vue'))
  },
  {
    path: '/address-manage',
    name: 'AddressManage',
    component: createAsyncComponent(() => import('../views/AddressManage.vue'))
  },
  {
    path: '/address-edit',
    name: 'AddressEdit',
    component: createAsyncComponent(() => import('../views/AddressEdit.vue'))
  },
  {
    path: '/address-select',
    name: 'AddressSelect',
    component: createAsyncComponent(() => import('../views/AddressSelect.vue'))
  },
  {
    path: '/share',
    name: 'SharePage',
    component: createAsyncComponent(() => import('../views/SharePage.vue'))
  },
  {
    path: '/google-login',
    name: 'GoogleLoginPage',
    component: createAsyncComponent(() => import('../views/GoogleLoginPage.vue'))
  },
  {
    path: '/coordinate-picker',
    name: 'CoordinatePicker',
    component: createAsyncComponent(() => import('../views/CoordinatePicker.vue'))
  },
  // 微信客服功能已禁用
  // {
  //   path: '/chat-demo',
  //   name: 'ChatDemo',
  //   component: createAsyncComponent(() => import('../views/ChatDemo.vue'))
  // },
  // Token login route hidden
  // {
  //   path: '/token-login',
  //   name: 'TokenLogin',
  //   component: createAsyncComponent(() => import('../views/TokenLogin.vue'))
  // }
]

const router = createRouter({
  history: createWebHistory(process.env.BASE_URL),
  routes
})

// 微信登录功能已移除

router.beforeEach((to, from, next) => {
  // 检查是否是Google登录回调
  const isGoogleCallback = to.query.google_login_success || to.query.google_login_error
  
  // 如果是Google登录回调，处理回调结果
  if (isGoogleCallback) {
    if (to.query.google_login_success) {
      // Google登录成功
      const redirectPath = localStorage.getItem('google_redirect_path') || '/'
      localStorage.removeItem('google_redirect_path')
      
      // 清除URL中的回调参数并跳转
      window.location.replace(redirectPath)
      return
    } else if (to.query.google_login_error) {
      // Google登录失败
      console.error('Google登录失败:', to.query.google_login_error)
      Toast.fail('Google登录失败')
      
      const redirectPath = localStorage.getItem('google_redirect_path') || '/'
      localStorage.removeItem('google_redirect_path')
      window.location.replace(redirectPath)
      return
    }
  }
  
  // 如果是从AddressSelect返回，不添加时间戳参数，避免干扰路由跳转
  if (from.name === 'AddressSelect' && to.query.from) {
    next();
  } else if (to.name && !to.query.tm) {
    next({ 
      ...to,
      query: { 
        ...to.query, 
        tm: new Date().getTime() 
      },
      replace: true
    });
  } else {
    next();
  }
});

export default router